import { flash } from "@xola/ui-kit";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { MerchandiseCatalogItemStatus, OversellPolicy } from "types";
import { MerchandiseItemsFormValues } from "../merchandiseHelper";
import { SetVariantContinueSellingModal } from "./SetVariantContinueSellingModal";
import { SetVariantLowInventoryModal } from "./SetVariantLowInventoryModal";
import { SetVariantPricesModal } from "./SetVariantPricesModal";
import { ToggleVariantStatusModal } from "./ToggleVariantStatusModal";
import { VariantBulkAction } from "./VariantActionsHeader";
import { useModal } from "@/hooks/useVisibility";

interface VariantBulkActionModalsProps {
    selectedCount: number;
    onUpdateStatus: (status: MerchandiseCatalogItemStatus) => Promise<void>;
    onUpdatePrice: (price: number) => Promise<void>;
    onUpdateOversellPolicy: (oversellPolicy: OversellPolicy) => Promise<void>;
    onUpdateLowInventory: (threshold: number) => Promise<void>;
}

export const useVariantBulkActionModals = ({
    selectedCount,
    onUpdateStatus,
    onUpdatePrice,
    onUpdateOversellPolicy,
    onUpdateLowInventory,
}: VariantBulkActionModalsProps) => {
    const { values } = useFormikContext<MerchandiseItemsFormValues>();
    const unpublishAllVariantsModal = useModal();
    const publishAllVariantsModal = useModal();
    const [activeAction, setActiveAction] = useState<VariantBulkAction | null>(null);

    const handleActionClick = (action: VariantBulkAction) => {
        if (selectedCount === 0) {
            return;
        }

        if (action === VariantBulkAction.PUBLISH) {
            publishAllVariantsModal.show();
            return;
        }

        if (action === VariantBulkAction.UNPUBLISH) {
            unpublishAllVariantsModal.show();
            return;
        }

        setActiveAction(action);
    };

    const handleApplyUnpublishAllVariants = async () => {
        try {
            await onUpdateStatus(MerchandiseCatalogItemStatus.DRAFT);
            unpublishAllVariantsModal.hide();
            flash.success("Variants updated");
        } catch {
            flash.error("Could not update variants");
        }
    };

    const handleApplyPublishAllVariants = async () => {
        const hasUnsavedPrices = Object.values(values.variantItems ?? {}).some((item) => item?.price !== undefined);

        if (hasUnsavedPrices) {
            flash.danger("Please save your changes before publishing.");
            return;
        }

        try {
            await onUpdateStatus(MerchandiseCatalogItemStatus.PUBLISHED);
            publishAllVariantsModal.hide();
            flash.success("Variants updated");
        } catch {
            flash.danger("Could not update variants");
        }
    };

    const modals = (
        <>
            <SetVariantPricesModal
                isOpen={activeAction === VariantBulkAction.SET_PRICE}
                selectedCount={selectedCount}
                onClose={() => setActiveAction(null)}
                onApply={onUpdatePrice}
            />

            <SetVariantLowInventoryModal
                isOpen={activeAction === VariantBulkAction.SET_LOW_INVENTORY}
                selectedCount={selectedCount}
                onClose={() => setActiveAction(null)}
                onApply={onUpdateLowInventory}
            />

            <SetVariantContinueSellingModal
                isOpen={activeAction === VariantBulkAction.SET_CONTINUE_SELLING}
                selectedCount={selectedCount}
                onClose={() => setActiveAction(null)}
                onApply={onUpdateOversellPolicy}
            />

            <ToggleVariantStatusModal
                isOpen={unpublishAllVariantsModal.isVisible}
                selectedCount={selectedCount}
                onClose={unpublishAllVariantsModal.hide}
                onApply={handleApplyUnpublishAllVariants}
            />

            <ToggleVariantStatusModal
                isOpen={publishAllVariantsModal.isVisible}
                selectedCount={selectedCount}
                action="publish"
                onClose={publishAllVariantsModal.hide}
                onApply={handleApplyPublishAllVariants}
            />
        </>
    );

    return { handleActionClick, modals };
};
